import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CompanyApi } from '@app/services/company-api';
import { CompanyInterface } from '@app/interface/company';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  PaginatedResponse,
  RecursiveInterface,
} from '@app/services/base-authenticated.service';
// import { LayoutComponent } from '@app/layout/layout.component';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { MatIconModule } from '@angular/material/icon';
import { TypeOptions } from '@app/interface/type-options';
import { MatSelectModule } from '@angular/material/select';
import { ChangeDetectorRef } from '@angular/core';
import { CountryOptions } from '@app/interface/country-options';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Component({
  selector: 'izy-company-detail',
  templateUrl: './company-detail.html',
  styleUrl: './company-detail.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    // LayoutComponent,
    BackHeaderComponent,
    MatSelectModule,
  ],
  standalone: true,
})
export class CompanyDetail implements OnInit {
  companyForm: FormGroup;
  companyId: number;
  isLoading = true;

  typeOptions: TypeOptions[] = [
    { code: 'AZIENDA', name: 'Azienda' },
    { code: 'PERSONA', name: 'Persona' },
    { code: 'PUBBLICA AMMINISTRAZIONE', name: 'Pubblica Amministrazione' },
    { code: 'CONDOMINIO', name: 'Condominio' },
  ];

  countryOptions: CountryOptions[] = [
    { code: 'IT', name: 'Italia' },
    { code: 'ES', name: 'Spagna' },
    { code: 'DE', name: 'Germania' },
    { code: 'FR', name: 'Francia' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyApi,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
  ) {
    this.companyForm = this.fb.group(
      {
        company_name: [
          '',
          [Validators.required, Validators.pattern('^[a-zA-Z0-9\\s.]*$')],
        ],
        iva_number: ['', [Validators.pattern('^[0-9]{11}$')]],
        ateco_code: [''],
        organization_id: [1],
        company_id: [''], // , Validators.required],
        addressee_code: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(7),
            Validators.pattern('^[A-Z0-9]*$'),
          ],
        ],
        address: [''],
        email_address: [''],
        pec: [''],
        c_f: ['', [Validators.pattern(/^([A-Z0-9]{11}|[A-Z0-9]{16})$/)]],
        country: [''],
        referent: [''],
        internal_code: [''],
        cellphone: [''],
        telephone: [''],
        notes: [''],
        type: [''],
        cap: ['', [Validators.pattern('^[0-9]{5}$')]],
        comune: [''],
        province: ['', [Validators.pattern('^[A-Z]{2}$')]],
        city: [''],
      },
      {
        validators: this.atLeastOneRequiredValidator,
      },
    );

    this.companyId = +this.route.snapshot.params['id'];
  }

  ngOnInit(): void {
    this.loadCompanyData();
  }

  public atLeastOneRequiredValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const ivaNumber = control.get('iva_number')?.value;
    const c_f = control.get('c_f')?.value;

    if (!ivaNumber && !c_f) {
      return { atLeastOneRequired: true };
    }
    return null;
  }

  private loadCompanyData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.companyService
      .getAllCompanies<CompanyInterface>(organization_id, {})
      .subscribe({
        next: (response: RecursiveInterface<CompanyInterface>) => {
          const company = response.results.find((c) => c.id === this.companyId);
          if (company) {
            this.companyForm.patchValue({
              ...company,
              organization_id: organization_id,
            });
            this.isLoading = false;
          } else {
            throw new Error('Company not found');
          }
        },
        error: (err: any) => {
          console.error('Error loading company:', err);
          this.showErrorMessage('Errore durante il caricamento.');
          this.router.navigate(['/company_table']);
        },
      });
  }

  provinceError: string = '';
  capsPattern = /^[A-Z]{2}$/; // Exactly 2 uppercase letters

  validateProvince(province: string | undefined): void {
    if (!province || province.trim() === '') {
      this.provinceError = 'La provincia è obbligatoria';
      return;
    }

    if (!this.capsPattern.test(province)) {
      this.provinceError =
        'La provincia deve essere di esattamente 2 lettere maiuscole';
      return;
    }

    this.provinceError = '';
  }

  onSubmit(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (this.companyForm.valid) {
      this.isLoading = true;

      // where you call the update method
      this.companyService
        .updateCompanyDetails(
          organization_id,
          this.companyId,
          this.companyForm.value,
        )
        .subscribe({
          next: (updatedCompany) => {
            this.isLoading = false;
            this.showSuccessMessage('Cliente aggiornato con successo!');
            // navigate back to the list
            // this.router.navigate(['/company_table']);
          },
          error: (err: any) => {
            this.isLoading = false;
            console.error('Error updating company:', err);
            this.showErrorMessage('Errore durante la modifica. Riprovare.');
          },
        });
    }

    // back to table
    this.onCancel();
    this.cdr.detectChanges();
  }

  onCancel(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/company_table`]);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
